import { toMajorUnit, toMinorUnit } from '@owner/currency'
import {
  type AmountFormatOptions,
  formatAmountCompact,
  formatMinorAmount,
} from '@owner/localization'

import { getActiveLocale } from '@/core/store/composables/useLocale'

import { currencySymbol } from './currencySymbol'
import { type FormatFn } from './Format.types'

export interface CurrencyFormatterOptions {
  cents: 'show-always' | 'show-non-zero' | 'hide'
  compactUnit?: 'auto' | 'thousands'
}

const defaultCurrencyFormatOptions: CurrencyFormatterOptions = {
  cents: 'show-always',
}

/**
 * Matches how `Intl` rounds a currency amount: halves go away from zero.
 * @param value
 */
const roundHalfAwayFromZero = (value: number): number =>
  Math.sign(value) * Math.round(Math.abs(value))

const thousandsFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
})

export const CurrencyFormatter = (
  value: number,
  options: AmountFormatOptions = { includeSymbol: true }
): string => formatMinorAmount(value, getActiveLocale(), options)

export const getCurrencyFormatter =
  (
    options: CurrencyFormatterOptions = defaultCurrencyFormatOptions
  ): FormatFn<number> =>
  (value: number) => {
    const config = getActiveLocale()

    if (options.cents === 'show-non-zero') {
      return formatAmountCompact(value, config)
    }

    if (options.cents === 'hide') {
      if (options.compactUnit === 'thousands') {
        // Composed rather than currency-formatted because no shared formatter
        // exposes a one-decimal amount. Safe while every operating locale
        // prefixes the symbol, which the "K" suffix already assumes.
        const inThousands = thousandsFormatter.format(toMajorUnit(value) / 1000)

        return `${currencySymbol(config)}${inThousands}K`
      }

      return formatAmountCompact(
        toMinorUnit(roundHalfAwayFromZero(toMajorUnit(value))),
        config
      )
    }

    return CurrencyFormatter(value)
  }
